/**
 * Format utilities for property data
 * 
 * Contains helper functions for formatting different property attributes.
 */

// Import translations
import { en } from "@shared/i18n/translations/en";
import { fr } from "@shared/i18n/translations/fr";
import { de } from "@shared/i18n/translations/de";
import { it } from "@shared/i18n/translations/it";

// Create translations object
const translations = {
  en,
  fr,
  de,
  it,
};

/**
 * Format full address from address components
 */
export function formatAddress(address) {
  if (!address) return "N/A";
  return address.street || "N/A";
}

/**
 * Format location (city, zip, country) from address components
 */
export function formatLocation(address) {
  if (!address) return "N/A";
  const zip = address.zip || "";
  const city = address.city || "";
  const country = address.country || "";

  return `${zip} ${city}, ${country}`;
}

/**
 * Get readable property type from API code
 * @param {string} type - Property type code from API
 * @param {string} language - Language code for translations
 */
export function getPropertyType(type, language = "en") {
  if (!type) return "N/A";
  
  const i18n = translations[language] || translations.en;
  
  if (i18n[type]) {
    return i18n[type];
  }
  
  // Fallback to English if no translation available
  return translations.en[type] || type;
}

/**
 * Format rooms and bathrooms information
 * @param {number} rooms - Number of rooms
 * @param {number} bathrooms - Number of bathrooms
 * @param {string} language - Language code for translations
 */
export function formatRooms(rooms, bathrooms, language = "en") {
  const roomCount = rooms || 0;
  const bathroomCount = bathrooms || 0;
  
  const i18n = translations[language] || translations.en;
  
  if (i18n.roomsFormat) {
    // Determine plural suffix for bathrooms based on language
    let bathroomSuffix = '';
    
    // Use specific plural rules for each language
    const suffix0 = i18n.bathrooms_plural_0 !== undefined ? i18n.bathrooms_plural_0 : '';
    const suffix1 = i18n.bathrooms_plural_1 !== undefined ? i18n.bathrooms_plural_1 : '';
    const suffixN = i18n.bathrooms_plural_n !== undefined ? i18n.bathrooms_plural_n : 's';
    
    if (bathroomCount === 0) {
      bathroomSuffix = suffix0;
    } else if (bathroomCount === 1) {
      bathroomSuffix = suffix1;
    } else {
      bathroomSuffix = suffixN;
    }
    
    // Replace placeholders with actual values
    return i18n.roomsFormat
      .replace('{{rooms}}', roomCount)
      .replace('{{bathrooms}}', bathroomCount)
      .replace('{{bathrooms_plural}}', bathroomSuffix)
      .replace('{{s}}', bathroomSuffix); // For backward compatibility
  }
  
  // Fallback to English if no translation available
  return `${roomCount} rooms, ${bathroomCount} bathroom${
    bathroomCount !== 1 ? "s" : ""
  }`;
}

/**
 * Format condition from API code
 * @param {string} condition - Condition code from API
 * @param {string} language - Language code for translations
 */
export function formatCondition(condition, language = "en") {
  if (!condition) return "N/A";
  
  const i18n = translations[language] || translations.en;
  
  if (i18n[condition]) {
    return i18n[condition];
  }
  
  // Fallback to English if no translation available
  return translations.en[condition] || condition;
}

/**
 * Format price with Swiss formatting (CHF 1'000'000)
 *
 * @param {number} value - Price value to format
 * @returns {string} Formatted price string
 */
export function formatPrice(value) {
  if (!value) return "CHF 0";
  return `CHF ${value.toLocaleString().replace(/,/g, "'")}`;
}

/**
 * Format price range with Swiss formatting
 *
 * @param {number} lower - Lower bound
 * @param {number} upper - Upper bound
 * @returns {string} Formatted price range
 */
export function formatPriceRange(lower, upper) {
  if (!lower || !upper) return "CHF 0";
  return `${formatPrice(lower)} - ${formatPrice(upper)}`;
}

/**
 * Format date from ISO string to human readable format
 *
 * @param {string} dateString - ISO date string
 * @param {string} language - Language code for formatting
 * @returns {string} Formatted date string
 */
export function formatDate(dateString, language = "en") {
  if (!dateString) return "";
  
  const date = new Date(dateString);
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  
  // Format date based on language
  if (language === "fr") {
    return date.toLocaleDateString("fr-CH", options);
  } else if (language === "de") {
    return date.toLocaleDateString("de-CH", options);
  } else if (language === "it") {
    return date.toLocaleDateString("it-CH", options);
  } else {
    return date.toLocaleDateString("en-GB", options);
  }
}